// Companion/blocks/studySetAnalytics.ts
//
// The study-set funnel (ORA-681), as one shape.
//
// Every event carries the same five properties, so served → launch → return →
// completed can be read as a funnel without joining on anything: `inline` is
// what separates a set the student could start in the thread from one that only
// ever had a button, and `surface` is where the thread was when they left it.

import { capture } from '@/utils/analytics';
import type { ChatStudySetBlockPayload } from '@/types/chatBlocks';
import type { ChatSurface } from '@/types/chatEnvelope';

export type StudySetEvent =
  | 'study_set_served'
  | 'study_set_launch'
  | 'study_set_return'
  | 'study_set_completed';

export interface StudySetEventProps {
  setId: string;
  kind: ChatStudySetBlockPayload['kind'];
  requestedCount: number;
  /** Whether the set was served with its items in the thread. */
  inline: boolean;
  surface: ChatSurface;
}

/**
 * Whether a set is served with its items in the thread.
 *
 * The count the set was asked for against the per-kind ceiling, never
 * `items.length`: a lazy set carries its items once it has been drawn (the GET
 * and the launch/return responses send them so the video playlist can be
 * built), so counting them would call a 24-question set inline the moment the
 * student launched it.
 */
export const isInlineStudySet = (
  payload: Pick<ChatStudySetBlockPayload, 'requestedCount' | 'inlineMax'>
): boolean => payload.requestedCount <= payload.inlineMax;

/** The event properties for a set, as the block knows it. */
export const studySetEventProps = (
  payload: Pick<
    ChatStudySetBlockPayload,
    'setId' | 'kind' | 'requestedCount' | 'inlineMax'
  >,
  surface: ChatSurface
): StudySetEventProps => ({
  setId: payload.setId,
  kind: payload.kind,
  requestedCount: payload.requestedCount,
  inline: isInlineStudySet(payload),
  surface,
});

/**
 * The backend reports the same four names with `source: 'server'`, so an
 * insight that counts one of them filters on `source` rather than counting
 * every set twice (ORA-1118).
 */
export const captureStudySetEvent = (
  event: StudySetEvent,
  props: StudySetEventProps
): void => {
  capture(event, { ...props, source: 'client' }, { withExam: true });
};
